/* eslint-disable prefer-template */
import axios from 'axios/index'
import { useDispatch } from 'react-redux'
import { format, isValid } from 'date-fns'

import config from '../../config'
import action, { actionFailed } from '../../util/action'
import createAsyncConst from '../../util/createAsyncConst'

export const REMOVE_BILLING = createAsyncConst('REMOVE_BILLING')
export const SUBMIT_BILLING = createAsyncConst('SUBMIT_BILLING')
export const FETCH_BILLING = createAsyncConst('FETCH_BILLING')
export const FETCH_LAUNDRY_BILLING_TYPES = createAsyncConst(
  'FETCH_LAUNDRY_BILLING_TYPES'
)
export const EDIT_BILLING = createAsyncConst('EDIT_BILLING')
export const BILL_PAID = createAsyncConst('BILL_PAID')
export const BANK_REPORT = createAsyncConst('BANK_REPORT')
export const BANK_STATEMENT = createAsyncConst('BANK_STATEMENT')

export function fetchBilling(filters = {}, rows, page) {
  return dispatch => {
    dispatch(action(FETCH_BILLING.ACTION))
    return axios
      .get(`${config.API_URL}/billing`, {
        params: {
          filters: JSON.stringify(filters),
          rows,
          page: page || 1,
        },
      })
      .then(resp => {
        dispatch(action(FETCH_BILLING.SUCCESS, { data: resp.data, filters }))
        return resp
      })
      .catch(e => dispatch(actionFailed(FETCH_BILLING, e)))
  }
}

export function fetchBillingV2(filters = {}, rows, page) {
  return dispatch => {
    dispatch(action(FETCH_BILLING.ACTION))
    return axios
      .get(`${config.API_URL}/v2/billing`, {
        params: {
          filters: JSON.stringify(filters),
          rows,
          page: page || 1,
        },
      })
      .then(resp => {
        dispatch(action(FETCH_BILLING.SUCCESS, { data: resp.data, filters }))
        return resp
      })
      .catch(e => dispatch(actionFailed(FETCH_BILLING, e)))
  }
}

export function makeRelatory(filters) {
  return dispatch => {
    return axios({
      url: `${config.API_URL}/v2/billing`,
      method: 'GET',
      responseType: 'blob',
      headers: { Accept: 'application/vnd.ms-excel' },
      params: {
        download: true,
        filters: JSON.stringify(filters),
      },
    })
      .then(resp => {
        const url = window.URL.createObjectURL(new Blob([resp.data]))
        const link = document.createElement('a')

        link.href = url
        link.setAttribute('download', 'bank_report.xlsx')
        document.body.appendChild(link)
        link.click()
        return resp
      })
      .catch(e => dispatch(actionFailed(BANK_REPORT, e)))
  }
}

export function exportDre(filters = {}, { summary = false } = {}) {
  return dispatch => {
    return axios({
      url: `${config.API_URL}/v2/billing`,
      method: 'GET',
      responseType: 'blob',
      headers: { Accept: 'application/vnd.ms-excel' },
      params: {
        download: true,
        filters: JSON.stringify(filters),
        ...(summary ? { group_by: 'billing_type', dre_summary: true } : {}),
      },
    })
      .then(resp => {
        const url = window.URL.createObjectURL(new Blob([resp.data]))
        const link = document.createElement('a')

        link.href = url
        link.setAttribute(
          'download',
          summary ? 'dre_geral.xlsx' : 'dre_detalhado.xlsx'
        )
        document.body.appendChild(link)
        link.click()
        return resp
      })
      .catch(e => dispatch(actionFailed(BANK_REPORT, e)))
  }
}

export function getBillings(filters) {
  return dispatch => {
    dispatch(action(FETCH_BILLING.ACTION))
    return axios
      .get(`${config.API_URL}/billing`, {
        params: filters,
      })
      .then(resp => {
        dispatch(action(FETCH_BILLING.SUCCESS, { data: resp.data, filters }))

        return resp.data
      })
      .catch(e => dispatch(FETCH_BILLING.FAILURE, e))
  }
}

export function getLaundryBillingType(filters) {
  return dispatch => {
    dispatch(action(FETCH_LAUNDRY_BILLING_TYPES.ACTION))
    return axios
      .get(`${config.API_URL}/billingType`, {
        params: { filters: JSON.stringify(filters) },
      })
      .then(resp => {
        dispatch(
          action(FETCH_LAUNDRY_BILLING_TYPES.SUCCESS, { data: resp.data })
        )
      })
      .catch(e => dispatch(FETCH_LAUNDRY_BILLING_TYPES.FAILURE, e))
  }
}

export function edit(id) {
  return dispatch => {
    dispatch(action(EDIT_BILLING.ACTION, { id }))
    return axios
      .get(`${config.API_URL}/billing/${id}`)
      .then(resp => {
        return resp.data.data
      })
      .then(data => dispatch(action(EDIT_BILLING.SUCCESS, data)))
      .catch(e => dispatch(actionFailed(EDIT_BILLING, e)))
  }
}

function submit(values, method) {
  const id = values.id ? `/${values.id}` : ''

  return dispatch => {
    dispatch(action(SUBMIT_BILLING.ACTION))
    return axios[method](`${config.API_URL}/billing${id}`, values)
      .then(resp => resp)
      .then(data => {
        dispatch(action(SUBMIT_BILLING.SUCCESS, data))
        return data
      })
      .catch(e => dispatch(actionFailed(SUBMIT_BILLING, e)))
  }
}

export function bankReport(filters = {}) {
  return dispatch => {
    dispatch(action(BANK_REPORT.ACTION))
    return axios
      .get(`${config.API_URL}/bank-report`, {
        params: {
          filters: JSON.stringify(filters),
        },
      })
      .then(resp => {
        dispatch(action(BANK_REPORT.SUCCESS, { data: resp.data, filters }))
        return resp
      })
      .catch(e => dispatch(actionFailed(BANK_REPORT, e)))
  }
}

// Normaliza os filtros vindos da URL para os params diretos que a rota
// /bank-statement espera: mantém bank_id/bill_type_id e converte as datas
// (ISO no filtro) para o formato Y-m-d exigido pela API. Chaves vazias e
// irrelevantes (ex.: tab) são descartadas.
const STATEMENT_DATE_KEYS = [
  'payment_date_start',
  'payment_date_end',
  'date',
  'date_to',
]

function normalizeStatementParams(filters = {}) {
  const params = {}

  ;['bank_id', 'bill_type_id'].forEach(key => {
    if (filters[key] !== undefined && filters[key] !== '') {
      params[key] = filters[key]
    }
  })

  STATEMENT_DATE_KEYS.forEach(key => {
    const value = filters[key]
    if (!value) return
    const date = new Date(value)
    params[key] = isValid(date) ? format(date, 'yyyy-MM-dd') : value
  })

  return params
}

export function bankStatement(filters = {}) {
  return dispatch => {
    dispatch(action(BANK_STATEMENT.ACTION))
    return axios
      .get(`${config.API_URL}/bank-statement`, {
        params: normalizeStatementParams(filters),
      })
      .then(resp => {
        dispatch(action(BANK_STATEMENT.SUCCESS, { data: resp.data, filters }))
        return resp
      })
      .catch(e => dispatch(actionFailed(BANK_STATEMENT, e)))
  }
}

export function exportBankStatement(filters = {}) {
  return dispatch => {
    // bank_id vai na URL; o restante dos filtros (bill_type_id + datas em
    // Y-m-d) vai no body da rota POST dedicada de exportação.
    const { bank_id: bankId, ...body } = normalizeStatementParams(filters)

    return axios({
      url: `${config.API_URL}/bank-statement-export/${bankId}`,
      method: 'POST',
      responseType: 'blob',
      headers: { Accept: 'application/vnd.ms-excel' },
      data: body,
    })
      .then(resp => {
        const url = window.URL.createObjectURL(new Blob([resp.data]))
        const link = document.createElement('a')

        link.href = url
        link.setAttribute('download', 'extrato_bancario.xlsx')
        document.body.appendChild(link)
        link.click()
        return resp
      })
      .catch(e => dispatch(actionFailed(BANK_STATEMENT, e)))
  }
}

export function makeReport(data, id) {
  return dispatch => {
    return axios({
      url: `${config.API_URL}/bank-report-export/` + id,
      method: 'POST',
      responseType: 'blob',
      headers: { Accept: 'application/vnd.ms-excel' },
      params: {
        filters: JSON.stringify(data),
      },
    })
      .then(resp => {
        const url = window.URL.createObjectURL(new Blob([resp.data]))
        const link = document.createElement('a')

        link.href = url
        link.setAttribute('download', 'bank_report.xlsx')
        document.body.appendChild(link)
        link.click()
        return resp
      })
      .catch(e => dispatch(actionFailed(BANK_REPORT, e)))
  }
}

export function post(values) {
  return submit(values, 'post')
}

export function update(values) {
  return submit(values, 'put')
}

export function remove(id) {
  return dispatch => {
    dispatch(action(REMOVE_BILLING.ACTION))
    return axios
      .delete(`${config.API_URL}/billing/${id}`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(REMOVE_BILLING.SUCCESS, data))
      })
      .catch(e => dispatch(actionFailed(REMOVE_BILLING, e)))
  }
}

export function billPaid(id, values, typeGeneric) {
  return dispatch => {
    dispatch(action(BILL_PAID.ACTION))

    return axios
      .post(`${config.API_URL}/billing/${id}/change-paid`, values)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(BILL_PAID.SUCCESS, data))
        return data
      })
      .catch(e => dispatch(actionFailed(BILL_PAID, e)))
  }
}

export function clearFilter() {
  return {
    type: 'CLEAR_BILLING_FILTERS',
  }
}

export function clearBilling() {
  return {
    type: 'CLEAR_BILLING',
  }
}
